import { useAuth } from '../../contexts/Auth'
import Button from '../Buttons'
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import { FEATURE_FLAGS, useAnnualPlan, usePricing } from '../../hooks/featureFlags'
import useCheckout from '../../hooks/checkout'
import { FaClock, FaPencil } from 'react-icons/fa6'
import UsersImg from '../../assets/images/users.png'
import { useMemo } from 'react'
import { IoSparkles } from 'react-icons/io5'
import { FaShieldAlt } from 'react-icons/fa'
import { LuCheck, LuCircleCheck, LuExternalLink } from 'react-icons/lu'

function Premium({
	clickUpgradeOverride,
	closeParentModal,
	setLoginModal,
	shorten,
	noMoreCredits = false,
	unlockEssay,
}: {
	clickUpgradeOverride?: () => void
	closeParentModal: () => void
	setLoginModal: (open: boolean) => void
	shorten?: boolean
	noMoreCredits?: boolean
	unlockEssay?: boolean
}) {
	const showFullAnnualPrice = useFeatureFlagVariantKey(FEATURE_FLAGS.SHOW_FULL_ANNUAL_PRICE)
	const limitedTrialFF = useFeatureFlagVariantKey(FEATURE_FLAGS.LIMITED_TRIAL)
	const paddleFF = useFeatureFlagVariantKey(FEATURE_FLAGS.PADDLE)
	const removePerMonthFF = useFeatureFlagVariantKey(FEATURE_FLAGS.REMOVE_PER_MONTH)
	const maskHumanizerFF = useFeatureFlagVariantKey(FEATURE_FLAGS.MASK_IN_UPGRADE_MODAL)
	const newUpgradeModalFF = useFeatureFlagVariantKey(FEATURE_FLAGS.NEW_UPGRADE_MODAL)
	const upgradeModalUpdatesFF = useFeatureFlagVariantKey(FEATURE_FLAGS.UPGRADE_MODAL_UPDATES)
	const posthog = usePostHog()
	const { checkout, loading } = useCheckout()
	const pricing = usePricing()
	const { currentUser } = useAuth() as any
	const [annualPlan, setAnnualPlan] = useAnnualPlan()

	function openNextStep(annualPlan: boolean) {
		if (clickUpgradeOverride) {
			clickUpgradeOverride?.()
			return
		}
		posthog.capture('upgrade-now-clicked')
		if (!currentUser) {
			setLoginModal(true)
			closeParentModal()
		} else {
			setAnnualPlan(annualPlan)
			checkout(annualPlan)
			if (paddleFF === 'test') {
				closeParentModal()
			}
		}
	}

	const annualSelectorV2 = useMemo(() => {
		// if (limitedTrialFF === 'test') {
		// 	return <LimitedTrial />
		// }
		if (limitedTrialFF === 'test') {
			return (
				<div className="w-full flex flex-col gap-3 mb-4 mt-8">
					<div
						className={`border-2 rounded-lg px-4 py-2 flex items-center justify-between cursor-pointer transition-all ${
							annualPlan ? 'border-gray-300' : 'border-link bg-[#eff6ff]'
						} relative`}
						onClick={() => setAnnualPlan(false)}
					>
						<div className="font-semibold flex items-center">Monthly</div>
						<div className="text-right">
							<div className="text-lg font-semibold">Free</div>
							<div className="text-sm text-gray-700">
								{pricing.currencySymbol}
								{pricing.monthly}/mo after trial
							</div>
						</div>
					</div>
					<div
						className={`border-2 rounded-lg px-4 py-2 flex items-center justify-between cursor-pointer transition-all ${
							!annualPlan ? 'border-gray-300' : 'border-link bg-[#eff6ff]'
						} relative`}
						onClick={() => setAnnualPlan(true)}
					>
						<div>
							<div className="font-semibold flex items-center">
								Annual
								<span
									className={`ml-2 bg-success-light text-success-dark px-2 py-0.5 rounded-full text-xs font-bold border-success-default border ${
										shorten ? 'hidden' : 'block'
									}`}
								>
									Save {pricing.discount}%
								</span>
							</div>
						</div>
						<div className="text-right">
							<div className="text-lg font-semibold">Free</div>
							<div className="text-sm text-gray-700">
								{pricing.currencySymbol}
								{pricing.yearlyTotal}/yr after trial
							</div>
						</div>
					</div>
				</div>
			)
		}
		if (showFullAnnualPrice === 'test') {
			return (
				<div className="w-full flex flex-col gap-3 mb-4 mt-8">
					<div
						className={`border-2 rounded-lg p-4 flex items-center justify-between cursor-pointer transition-all ${
							annualPlan ? 'border-gray-300' : 'border-link bg-[#eff6ff]'
						} relative`}
						onClick={() => setAnnualPlan(false)}
					>
						<div className="font-semibold flex items-center">Monthly</div>
						<div>
							<span className="text-gray-700 text-base">{pricing.currencySymbol}</span>
							<span className="text-lg font-medium">{pricing.monthly}</span>
							{removePerMonthFF !== 'test' && <span className="text-gray-700 text-base">/mo</span>}
						</div>
					</div>
					<div
						className={`border-2 rounded-lg p-4 flex items-center justify-between cursor-pointer transition-all ${
							!annualPlan ? 'border-gray-300' : 'border-link bg-[#eff6ff]'
						} relative`}
						onClick={() => setAnnualPlan(true)}
					>
						<div>
							<div className="font-semibold flex items-center">
								Annual
								<span
									className={`ml-2 bg-success-light text-success-dark px-2 py-0.5 rounded-full text-xs font-bold border-success-default border ${
										shorten ? 'hidden' : 'block'
									}`}
								>
									Save {pricing.discount}%
								</span>
							</div>
						</div>
						<div>
							<span className="text-gray-700 text-base">{pricing.currencySymbol}</span>
							<span className="text-lg font-medium">{pricing.yearlyTotal}</span>
							{removePerMonthFF !== 'test' && <span className="text-gray-700 text-base">/mo</span>}
						</div>
					</div>
				</div>
			)
		}
		return (
			<div className="w-full flex flex-col gap-3 mb-4 mt-8">
				<div
					className={`border-2 rounded-lg p-4 flex items-center justify-between cursor-pointer transition-all ${
						!annualPlan ? 'border-gray-300' : 'border-link bg-[#eff6ff]'
					} relative`}
					onClick={() => setAnnualPlan(true)}
				>
					<div>
						<div className="font-semibold flex items-center">
							Annual
							<span
								className={`ml-2 bg-success-light text-success-dark px-2 py-0.5 rounded-full text-xs font-bold border-success-default border ${
									shorten ? 'hidden' : 'block'
								}`}
							>
								Save {pricing.discount}%
							</span>
						</div>
					</div>
					<div>
						<span className="text-gray-700 text-base">{pricing.currencySymbol}</span>
						<span className="text-lg font-medium">{pricing.yearly}</span>
						{removePerMonthFF !== 'test' && <span className="text-gray-700 text-base">/mo</span>}
					</div>
				</div>
				<div
					className={`border-2 rounded-lg p-4 flex items-center justify-between cursor-pointer transition-all ${
						annualPlan ? 'border-gray-300' : 'border-link bg-[#eff6ff]'
					} relative`}
					onClick={() => setAnnualPlan(false)}
				>
					<div className="font-semibold flex items-center">Monthly</div>
					<div>
						<span className="text-gray-700 text-base">{pricing.currencySymbol}</span>
						<span className="text-lg font-medium">{pricing.monthly}</span>
						{removePerMonthFF !== 'test' && <span className="text-gray-700 text-base">/mo</span>}
					</div>
				</div>
			</div>
		)
	}, [
		annualPlan,
		pricing.currencySymbol,
		pricing.discount,
		pricing.monthly,
		pricing.yearly,
		pricing.yearlyTotal,
		removePerMonthFF,
		showFullAnnualPrice,
		shorten,
	])

	if (newUpgradeModalFF === 'test') {
		return (
			<div className={`${shorten ? 'md:pr-0 pr-0 md:pl-4 pl-0' : 'md:px-6 px-3 '}`}>
				<h2 className={`text-2xl font-bold mb-2 ${shorten ? 'md:mt-0' : 'md:mt-5  pt-6'} md:pt-0 mt-0`}>
					{limitedTrialFF === 'test'
						? upgradeModalUpdatesFF === 'test'
							? 'Try Unlimited — 7 Days Free'
							: 'Get Unlimited — 7 Days Free'
						: noMoreCredits
						? "You've reached your limit"
						: unlockEssay
						? 'Unlock Your Full Essay'
						: 'Upgrade your account'}
				</h2>
				<div className="flex flex-col">
					<div className="grid grid-cols-4">
						<div className="col-span-2 font-medium text-gray-500"></div>
						<div className="col-span-1 text-center font-medium text-sm">Free</div>
						<div className="col-span-1 text-center font-medium text-sm text-[#245db9] bg-[#eff6ff] px-2 py-1 rounded-t-lg">
							Upgrade
						</div>
					</div>
					{[
						{
							title: 'Unlimited Essays',
							free: null,
							unlimited: true,
							icon: LuCircleCheck({ className: 'w-4 h-4 text-gray-500' }),
						},
						{
							title: 'Personalized Writing Style',
							free: null,
							unlimited: true,
							icon: LuCircleCheck({ className: 'w-4 h-4 text-gray-500' }),
						},
						{
							title: 'Unlimited AI Humanizer',
							free: null,
							unlimited: true,
							icon: LuCircleCheck({ className: 'w-4 h-4 text-gray-500' }),
						},
						{
							title: 'Unlimited AI Detector',
							free: null,
							unlimited: true,
							icon: LuCircleCheck({ className: 'w-4 h-4 text-gray-500' }),
						},
						{
							title: 'Up to 3000 words',
							free: null,
							unlimited: true,
							icon: LuCircleCheck({ className: 'w-4 h-4 text-gray-500' }),
						},
					].map((item, index) => (
						<div className={`grid grid-cols-4 text-sm ${index === 4 ? '' : 'border-b border-gray-300'}`} key={index}>
							<div className="col-span-2 py-2 flex items-center gap-2 whitespace-nowrap">
								<span className="shrink-0">{item.icon}</span>
								{item.title}
							</div>
							<div className="col-span-1 text-center text-gray-500 py-2">{item.free}</div>
							<div
								className={`col-span-1 text-center font-medium bg-[#eff6ff] px-2 py-2 text-primary-600 ${
									index === 4 ? 'rounded-b-lg' : ''
								}`}
							>
								{item.unlimited === true ? LuCheck({ className: 'mx-auto text-[#245db9] w-4 h-4 ' }) : item.unlimited}
							</div>
						</div>
					))}
				</div>
				{annualSelectorV2}
				{pricing.countryCode !== 'US' && (
					<div
						className="text-xs rounded-lg p-2 px-4 bg-success-light mb-2"
						style={{
							animation: 'subtle-bounce 1s infinite',
						}}
					>
						You’re in <b>{pricing.countryCode}</b> — enjoy your 30% local discount
					</div>
				)}
				{upgradeModalUpdatesFF === 'test' ? (
					<Button
						type="default"
						htmlType="submit"
						onClick={() => openNextStep(annualPlan)}
						className="w-full bg-blue-600 text-white font-bold py-1 rounded-lg text-lg mb-2 mt-4 shadow flex items-center justify-center"
					>
						{LuExternalLink({ className: 'w-5 h-5 mr-2' })}
						<span>{loading ? 'Redirecting...' : limitedTrialFF === 'test' ? 'Continue' : 'Upgrade Now'}</span>
					</Button>
				) : (
					<Button
						type="default"
						htmlType="submit"
						onClick={() => openNextStep(annualPlan)}
						className="w-full bg-blue-600 text-white font-bold py-1 rounded-lg text-lg mb-2 mt-4 shadow"
					>
						<span>{loading ? 'Loading...' : limitedTrialFF === 'test' ? 'Try Unlimited' : 'Upgrade Now'}</span>
					</Button>
				)}
				<div className="flex gap-2 items-center justify-center pb-4 mt-2">
					<img src={UsersImg} alt="Users" className="h-10" />
					<div className="flex flex-col gap-0 items-start">
						<div className="m-0 text-sm text-gray-700 text-left">
							Join over 3 million students writing with The Good AI
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={`md:px-6 px-3 ${shorten ? 'md:px-0 px-0' : ''}`}>
			<h2 className={`text-2xl font-bold mb-2 ${shorten ? 'md:mt-0' : 'md:mt-5  pt-6'} md:pt-0 mt-0`}>
				{noMoreCredits ? "You've reached your limit" : unlockEssay ? 'Unlock Your Full Essay' : 'Upgrade to Unlimited'}
			</h2>
			<div className={`mb-4 w-full flex flex-col gap-4 ${shorten ? 'mt-6' : 'mt-3'}`}>
				{[
					{
						icon: IoSparkles,
						title: 'Unlimited Essays',
						description: 'No limits on the number of essays you can write and edit',
					},
					maskHumanizerFF === 'test'
						? {
								icon: ({ className }: { className?: string }) => (
									<img
										src="https://www.mask.ai/_next/static/media/logo-updated.270a7ed5.svg"
										alt="Mask AI"
										className={className}
										style={{ width: '1.1em', height: '1.1em' }}
									/>
								),
								title: 'AI Humanizer & Detector',
								description: 'Get unlimited AI humanizing and detection provided by Mask AI, a $40/mo value',
						  }
						: {
								icon: FaShieldAlt,
								title: 'AI Humanizer and Detector',
								description: 'Protect your work from plagiarism and AI detection',
						  },
					{
						icon: FaClock,
						title: 'Save 3-5 hours per paper',
						description: 'Focus on research and insights, not writing',
					},
					{
						icon: FaPencil,
						title: 'Personalized Writing Style',
						description: 'Tailored to your unique voice and preferences',
					},
				].map((item) => (
					<div className="flex gap-2.5 justify-start">
						{item.icon({ className: 'text-gray-600 text-base mt-1 shrink-0' })}
						<div className="flex flex-col text-base">
							<div className="font-semibold ">{item.title}</div>
							<div className={`text-gray-600 font-normal ${shorten ? 'hidden' : ''}`}>{item.description}</div>
						</div>
					</div>
				))}
			</div>
			{annualSelectorV2}
			<div
				className="text-xs rounded-lg p-2 px-4 bg-success-light mb-2"
				style={{
					animation: 'subtle-bounce 1s infinite',
				}}
			>
				You’re in <b>{pricing.countryCode}</b> — enjoy your 30% local discount
			</div>
			<Button
				type="default"
				htmlType="submit"
				onClick={() => openNextStep(annualPlan)}
				className="w-full bg-blue-600 text-white font-bold py-1 rounded-lg text-lg mb-2 mt-4 shadow"
			>
				<span>{loading ? 'Loading...' : 'Upgrade Now'}</span>
			</Button>
			<div className="flex gap-2 items-center justify-center pb-4 mt-2">
				<img src={UsersImg} alt="Users" className="h-10" />
				<div className="flex flex-col gap-0 items-start">
					<div className="m-0 text-sm text-gray-700 text-left">
						Join over 3 million students writing with The Good AI
					</div>
				</div>
			</div>
		</div>
	)
}

export default Premium
